import { useCallback, useSyncExternalStore } from 'react';

// A single property-scope selection shared across every property-scoped page
// (Leases, Banners, Applicants). Each page used to keep its own local state that
// reset to the first property on navigation, so picking a property on one page
// was lost when moving to another. Persisting to localStorage keeps the choice
// stable across menu navigation AND full reloads; useSyncExternalStore keeps any
// mounted pages (and other tabs, via the storage event) in sync live.
const KEY = 'tp-admin.selectedPropertyId';
const listeners = new Set();

function read() {
  try {
    return localStorage.getItem(KEY) || '';
  } catch {
    return '';
  }
}

function subscribe(callback) {
  listeners.add(callback);
  window.addEventListener('storage', callback);
  return () => {
    listeners.delete(callback);
    window.removeEventListener('storage', callback);
  };
}

export function useSelectedProperty() {
  const value = useSyncExternalStore(subscribe, read, read);
  const setValue = useCallback(id => {
    try {
      localStorage.setItem(KEY, id ?? '');
    } catch {
      // ignore write failures (private mode / quota); selection just won't persist
    }
    listeners.forEach(l => l());
  }, []);
  return [value, setValue];
}
